import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { Checklist as ChecklistIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { PROJECT_ACCENT_VAR } from "@/components/project/ProjectThemeProvider";
import GalleryPasswordGate from "./GalleryPasswordGate";
import GalleryHeader from "./GalleryHeader";
import GalleryGrid from "./GalleryGrid";
import GalleryConnectionStatus from "./GalleryConnectionStatus";
import GalleryNewVideoToast from "./GalleryNewVideoToast";
import GalleryVideoModal from "./GalleryVideoModal";
import GallerySelectionBar from "./GallerySelectionBar";
import { useGallerySSE } from "@/hooks/useGallerySSE";
import { useNotificationSound } from "@/hooks/useNotificationSound";
import { fetchPublicGallery, downloadZip } from "@/api/galleriesApi";
import type { GalleryVideo } from "./GalleryVideoCard";

const MAX_SELECTION = 50;

/**
 * Sanitize a URL for safe use in CSS `url()`.
 * Strips characters that could break out of the CSS url() context.
 */
function safeCssUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  // Reject URLs containing characters that can escape CSS url() context
  if (/[)"';{}]/.test(url)) return null;
  return url;
}

interface ProjectColorsData {
  button_gradient_start?: string;
  button_gradient_end?: string;
  button_text_color?: string;
  text_color?: string;
  form_background?: string;
}

interface GalleryData {
  id: string;
  project_id: string;
  name: string;
  slug: string;
  display_mode: string;
  grid_columns: number;
  grid_rows: number;
  autoplay: boolean;
  muted: boolean;
  qr_enabled: boolean;
  qr_position: string;
  qr_size: string;
  title?: string | null;
  show_header: boolean;
  allow_download: boolean;
  project_name: string;
  project_logo_url?: string | null;
  project_banner_url?: string | null;
  project_background_url?: string | null;
  project_colors?: ProjectColorsData | null;
  videos: GalleryVideo[];
}

interface PasswordMeta {
  gallery_name: string;
  project_logo_url?: string | null;
  project_background_url?: string | null;
  project_colors?: ProjectColorsData | null;
}

interface LiveGalleryPageProps {
  slug: string;
}

export default function LiveGalleryPage({ slug }: LiveGalleryPageProps) {
  const { t } = useTranslation("gallery");
  const theme = useTheme();
  const [gallery, setGallery] = useState<GalleryData | null>(null);
  const galleryRef = useRef<GalleryData | null>(null);
  const [videos, setVideos] = useState<GalleryVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordMeta, setPasswordMeta] = useState<PasswordMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deactivated, setDeactivated] = useState(false);
  const [newVideoForToast, setNewVideoForToast] = useState<GalleryVideo | null>(
    null,
  );

  // Modal state
  const [selectedVideo, setSelectedVideo] = useState<GalleryVideo | null>(null);

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const playNotificationSound = useNotificationSound({
    muted: gallery?.muted ?? true,
  });

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPublicGallery(slug);

      if (res.status === 401) {
        const data = await res.json();
        if (data.detail?.password_required) {
          setPasswordRequired(true);
          setPasswordMeta({
            gallery_name: data.detail.gallery_name,
            project_logo_url: data.detail.project_logo_url,
            project_background_url: data.detail.project_background_url,
            project_colors: data.detail.project_colors,
          });
        }
        setLoading(false);
        return;
      }

      if (res.status === 404) {
        setError(t("public.galleryNotFound"));
        setLoading(false);
        return;
      }

      if (!res.ok) {
        setError(t("public.loadingError"));
        setLoading(false);
        return;
      }

      const data: GalleryData = await res.json();
      setGallery(data);
      galleryRef.current = data;
      setVideos(data.videos);
      setPasswordRequired(false);
      setPasswordMeta(null);
    } catch {
      setError(t("public.connectionError"));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  // Fetch on mount
  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // SSE: handle new videos
  const handleNewVideo = useCallback(
    (video: GalleryVideo) => {
      setVideos((prev) => {
        // Avoid duplicates
        if (prev.some((v) => v.id === video.id)) return prev;
        const updated = [video, ...prev];
        // In fixed mode, cap array to grid capacity to prevent memory leak
        const g = galleryRef.current;
        if (g && g.display_mode === "fixed") {
          const maxVideos = g.grid_columns * g.grid_rows;
          return updated.slice(0, maxVideos);
        }
        return updated;
      });
      // Toast notification (scroll mode only — GalleryNewVideoToast handles visibility)
      setNewVideoForToast(video);
      // Sound notification
      playNotificationSound();
    },
    [playNotificationSound],
  );

  // SSE: gallery settings updated in real-time
  const handleGalleryUpdated = useCallback(
    (config: Record<string, unknown>) => {
      setGallery((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, ...config };
        galleryRef.current = updated;
        return updated;
      });
    },
    [],
  );

  // SSE: gallery deactivated
  const handleGalleryDeactivated = useCallback(() => {
    setDeactivated(true);
  }, []);

  // Toast dismiss — stable reference to avoid resetting auto-dismiss timer
  const handleToastDismiss = useCallback(() => {
    setNewVideoForToast(null);
  }, []);

  // Connect SSE only when gallery is loaded
  const { connectionStatus } = useGallerySSE({
    slug: gallery && !deactivated ? slug : null,
    onVideoNew: handleNewVideo,
    onGalleryUpdated: handleGalleryUpdated,
    onGalleryDeactivated: handleGalleryDeactivated,
  });

  // --- Modal handlers ---
  const handleVideoClick = useCallback((video: GalleryVideo) => {
    setSelectedVideo(video);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedVideo(null);
  }, []);

  // --- Selection handlers ---
  const handleToggleSelect = useCallback(
    (videoId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(videoId)) {
          next.delete(videoId);
        } else {
          if (next.size >= MAX_SELECTION) {
            enqueueSnackbar(t("public.maxSelection", { max: MAX_SELECTION }), {
              variant: "warning",
            });
            return prev;
          }
          next.add(videoId);
        }
        return next;
      });
    },
    [enqueueSnackbar, t],
  );

  const handleSelectAll = useCallback(() => {
    const all = new Set(videos.slice(0, MAX_SELECTION).map((v) => v.id));
    setSelectedIds(all);
  }, [videos]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleExitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleDownloadSelection = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDownloading(true);

    // NOTE: Download tracking is handled server-side in the download-zip endpoint.
    // Do NOT add frontend trackEvent here — it would cause double-counting.

    try {
      const response = await downloadZip(slug, Array.from(selectedIds));

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        enqueueSnackbar(err?.detail || t("public.downloadError"), {
          variant: "error",
        });
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ||
        "videos.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(t("public.downloadError"), { variant: "error" });
    } finally {
      setDownloading(false);
    }
  }, [selectedIds, slug, enqueueSnackbar, t]);

  // Escape key to close selection mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectionMode) {
        handleExitSelection();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectionMode, handleExitSelection]);

  // --- Accent color ---
  const accentColor =
    gallery?.project_colors?.button_gradient_start || theme.palette.primary.main;
  const passwordAccentColor =
    passwordMeta?.project_colors?.button_gradient_start || theme.palette.primary.main;
  const backgroundUrl = safeCssUrl(gallery?.project_background_url);
  const hasBackground = !!backgroundUrl;

  // Password gate
  if (passwordRequired && passwordMeta) {
    return (
      <GalleryPasswordGate
        galleryName={passwordMeta.gallery_name}
        logoUrl={passwordMeta.project_logo_url}
        backgroundUrl={safeCssUrl(passwordMeta.project_background_url)}
        accentColor={passwordAccentColor}
        slug={slug}
        onAuthenticated={fetchGallery}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "rgba(255,255,255,0.5)" }} />
      </Box>
    );
  }

  // Error state
  if (error || !gallery) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
          {error || t("public.galleryNotFound")}
        </Typography>
      </Box>
    );
  }

  // Deactivated state
  if (deactivated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.7)" }}>
          {t("public.galleryInactive")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#000",
        overflow: gallery.display_mode === "fixed" ? "hidden" : "auto",
        position: "relative",
        [PROJECT_ACCENT_VAR]: accentColor,
      }}
    >
      {/* Background image layer */}
      {hasBackground && (
        <>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: 0,
            }}
          />
          {/* Dark overlay for contrast */}
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0, 0, 0, 0.75)",
              zIndex: 0,
            }}
          />
        </>
      )}

      {/* Content */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {gallery.show_header && (
          <GalleryHeader
            title={gallery.title}
            projectName={gallery.project_name}
            projectLogoUrl={gallery.project_logo_url}
          />
        )}

        {/* Selection mode toggle button */}
        {gallery.allow_download && !selectionMode && (
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 1200,
            }}
          >
            <Button
              variant="contained"
              startIcon={<ChecklistIcon />}
              onClick={() => setSelectionMode(true)}
              sx={{
                bgcolor: "rgba(30,30,30,0.9)",
                backdropFilter: "blur(10px)",
                "&:hover": { bgcolor: "rgba(50,50,50,0.95)" },
                textTransform: "none",
                borderRadius: 3,
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              }}
            >
              {t("public.select")}
            </Button>
          </Box>
        )}

        <GalleryGrid
          videos={videos}
          displayMode={gallery.display_mode}
          gridColumns={gallery.grid_columns}
          gridRows={gallery.grid_rows}
          autoplay={gallery.autoplay}
          muted={gallery.muted}
          qrEnabled={gallery.qr_enabled}
          qrPosition={gallery.qr_position}
          qrSize={gallery.qr_size}
          showHeader={gallery.show_header}
          allowDownload={gallery.allow_download}
          gallerySlug={slug}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onVideoClick={handleVideoClick}
          hasBackground={hasBackground}
        />

        <GalleryConnectionStatus status={connectionStatus} />

        {gallery.display_mode === "scroll" && (
          <GalleryNewVideoToast
            video={newVideoForToast}
            onDismiss={handleToastDismiss}
          />
        )}

        {/* Fullscreen video modal */}
        <GalleryVideoModal
          video={selectedVideo}
          open={selectedVideo !== null}
          onClose={handleCloseModal}
          allowDownload={gallery.allow_download}
          gallerySlug={slug}
        />

        {/* Selection bar */}
        <GallerySelectionBar
          visible={selectionMode}
          selectedCount={selectedIds.size}
          totalCount={videos.length}
          downloading={downloading}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onDownload={handleDownloadSelection}
          onClose={handleExitSelection}
        />
      </Box>
    </Box>
  );
}
